<template>
  <div class="rt-root">
    <div class="rt-page">
      <div class="rt-head">
        <h1 class="rt-title">{{ $t("RatingTitle") }}</h1>
        <p class="rt-sub">
          {{
            $t("RatingPageDesc") 
          }}
        </p>
      </div>

      <div class="rt-tabs">
        <button
          class="rt-tab"
          :class="{ active: tab === 'submit' }"
          @click="tab = 'submit'"
        >
          <i class="far fa-star"></i>
          {{ $t("RatingTabSubmit") }}
        </button>
         <button
          class="rt-tab"
          :class="{ active: tab === 'search' }"
          @click="tab = 'search'"
        >
          <i class="fas fa-search"></i>
          {{ $t("RatingTabSearch")  }}
        </button>
        <button
          class="rt-tab"
          :class="{ active: tab === 'history' }"
          @click="tab = 'history'"
        >
          <i class="far fa-clock"></i>
          {{ $t("RatingTabHistory") }}
          <span v-if="history.length" class="rt-badge-cnt">{{ history.length }}</span>
        </button>
       
      </div>

      <!-- TAB 1: SUBMIT -->
      <div v-if="tab === 'submit'">
        <section class="rt-card">
          <div class="rt-card-head">
            <h2>
              <span class="rt-step">1</span>
              {{ $t("RatingStep1")}}
            </h2>
            <span class="rt-hint">
              {{ $t("RatingByNumberOnly")  }}
            </span>
          </div>
          <div class="rt-card-body">
            <div class="rt-field">
              <label>{{ $t("InspectionNumber")}}</label>
              <div class="rt-input-wrap">
                <i class="fas fa-hashtag lead"></i>
                <input
                  v-model="numberQuery"
                  type="text"
                  placeholder="TX-2026-000000"
                  :disabled="searching"
                  @keyup.enter="searchByNumber"
                />
                <button
                  class="rt-go"
                  :disabled="searching"
                  @click="searchByNumber"
                >
                  <i v-if="searching" class="fas fa-spinner fa-spin"></i>
                  <span v-else>{{ $t("Search") }}</span>
                </button>
              </div>
            </div>

            <div v-if="searchError" class="rt-error">
              <i class="fas fa-exclamation-circle"></i> {{ searchError }}
            </div>

            <div v-if="found" class="rt-found">
              <div class="rt-found-icon">
                <i class="fas fa-clipboard-check"></i>
              </div>
              <div class="rt-found-info">
                <div class="rt-found-code">{{ found.code }}</div>
                <div class="rt-found-desc">{{ found.desc }}</div>
                <div v-if="found.contractor" class="rt-found-sub">
                  <i class="far fa-building"></i> {{ found.contractor }}
                </div>
                <div v-if="found.inspectionOrganization" class="rt-found-sub">
                  <i class="fas fa-landmark"></i>
                  <span class="rt-found-sub-lbl">
                    {{ $t("InspectionOrganization")  }}:
                  </span>
                  {{ found.inspectionOrganization }}
                </div>
                <div class="rt-found-meta">
                  <i class="far fa-calendar-alt"></i> {{ found.date }}
                </div>
              </div>
              <div class="rt-found-badge">{{ $t("Found")  }}</div>
            </div>
          </div>
        </section>

        <section v-if="found" class="rt-card">
          <div class="rt-card-head">
            <h2>
              <span class="rt-step">2</span>
              {{ $t("RatingStep2") || "Baholash" }}
            </h2>
            <span class="rt-hint">
              {{ $t("RatingStep2Hint") || "5 ballik tizim" }}
            </span>
          </div>
          <div class="rt-card-body">
            <!-- Inspection rating -->
            <div class="rt-rate-block">
              <div class="rt-who">
                <div class="rt-who-icon"><i class="fas fa-clipboard-check"></i></div>
                <div>
                  <div class="rt-who-lbl">
                    {{ $t("InspectionProcess") }}
                  </div>
                  <div class="rt-who-nm">{{ found.code }}</div>
                </div>
              </div>
              <div class="rt-stars">
                <button
                  v-for="n in 5"
                  :key="'i' + n"
                  class="rt-star"
                  :class="{ filled: n <= inspectionRating }"
                  @click="inspectionRating = n"
                >
                  <i class="fas fa-star"></i>
                </button>
                <strong class="rt-stars-val">{{ inspectionRating }} / 5</strong>
              </div>
              <textarea
                v-model="inspectionComment"
                class="rt-comment"
                maxlength="500"
                :placeholder="
                  $t('InspectionCommentPh') 
                "
              ></textarea>
            </div>

            <!-- Inspectors collective rating -->
            <div v-if="inspectors.length" class="rt-rate-block">
              <div class="rt-who">
                <div class="rt-who-icon"><i class="fas fa-users"></i></div>
                <div>
                  <div class="rt-who-lbl">
                    {{
                      $t("InspectorsInThisInspection") ||
                        "Tekshiruvda qatnashgan tekshiruvchilar"
                    }}
                  </div>
                  <div class="rt-who-nm">
                    {{ $t("RateAllInspectorsTogether") || "Hammasi uchun umumiy baho" }}
                  </div>
                </div>
              </div>

              <ul class="rt-insp-names">
                <li v-for="ins in inspectors" :key="ins.id">
                  <i class="far fa-user"></i> {{ ins.name }}
                </li>
              </ul>

              <div class="rt-stars">
                <button
                  v-for="n in 5"
                  :key="'all' + n"
                  class="rt-star"
                  :class="{ filled: n <= inspectorsRating }"
                  @click="inspectorsRating = n"
                >
                  <i class="fas fa-star"></i>
                </button>
                <strong class="rt-stars-val">{{ inspectorsRating }} / 5</strong>
              </div>
              <textarea
                v-model="inspectorsComment"
                class="rt-comment"
                maxlength="500"
                :placeholder="
                  $t('InspectorCommentPh') ||
                    'Tekshiruvchilar haqida izoh (ixtiyoriy)'
                "
              ></textarea>
            </div>

            <div v-else class="rt-insp-empty">
              <i class="far fa-user"></i>
              {{ $t("NoInspectorsFound") || "Tekshiruvchilar topilmadi" }}
            </div>
          </div>

          <div v-if="submitError" class="rt-error" style="margin: 0 22px 12px">
            <i class="fas fa-exclamation-circle"></i> {{ submitError }}
          </div>

          <div class="rt-actions">
            <div class="rt-note">
              <i class="fas fa-info-circle"></i>
              {{
                $t("RatingNote") ||
                  "Baholash yuborilgandan keyin uni o'zgartirish mumkin emas"
              }}
            </div>
            <div class="rt-btns">
              <button
                class="rt-btn ghost"
                :disabled="submitting"
                @click="resetForm"
              >
                {{ $t("Cancel") || "Bekor qilish" }}
              </button>
              <button
                class="rt-btn primary"
                :disabled="!canSubmit || submitting"
                @click="submit"
              >
                <i v-if="submitting" class="fas fa-spinner fa-spin"></i>
                <template v-else>
                  {{ $t("Submit") || "Yuborish" }}
                  <i class="fas fa-arrow-right"></i>
                </template>
              </button>
            </div>
          </div>
        </section>
      </div>

      <!-- TAB 2: HISTORY -->
      <div v-else-if="tab === 'history'">
        <div v-if="loadingHistory" class="rt-empty">
          <i class="fas fa-spinner fa-spin"></i>
          <p>{{ $t("Loading") || "Yuklanmoqda..." }}</p>
        </div>
        <div v-else-if="!history.length" class="rt-empty">
          <i class="far fa-clipboard"></i>
          <p>{{ $t("NoRatingHistory") || "Baholash tarixi bo'sh" }}</p>
        </div>
        <div v-else class="rt-history">
          <div v-for="h in history" :key="h.id" class="rt-h-card">
            <div class="rt-h-top">
              <div>
                <div class="rt-h-code">{{ h.code }}</div>
                <div class="rt-h-desc">{{ h.desc }}</div>
              </div>
              <div class="rt-h-date">
                <i class="far fa-calendar-alt"></i> {{ h.submittedAt }}
              </div>
            </div>

            <div class="rt-h-row">
              <span class="rt-h-lbl">
               {{ h.ratingTypeName || $t("InspectionProcess") }}
              </span>
              <span class="rt-h-stars">
                <i
                  v-for="n in 5"
                  :key="'h' + h.id + 'i' + n"
                  class="fas fa-star"
                  :class="{ on: n <= h.inspectionRating }"
                ></i>
                <strong>{{ h.inspectionRating }}/5</strong>
              </span>
            </div>
            <div v-if="h.inspectionComment" class="rt-h-cm">
              "{{ h.inspectionComment }}"
            </div>

            <div v-if="h.inspectorsRating" class="rt-h-inspectors">
              <div class="rt-h-row">
                <span class="rt-h-lbl">
                  {{
                    h.inspectorsTypeName ||
                      $t("InspectorsInThisInspection") ||
                      "Tekshiruvchilar"
                  }}
                </span>
                <span class="rt-h-stars">
                  <i
                    v-for="n in 5"
                    :key="'h' + h.id + 'all' + n"
                    class="fas fa-star"
                    :class="{ on: n <= h.inspectorsRating }"
                  ></i>
                  <strong>{{ h.inspectorsRating }}/5</strong>
                </span>
              </div>
              <div v-if="h.inspectorsComment" class="rt-h-cm">
                "{{ h.inspectorsComment }}"
              </div>
              <div v-if="h.inspectorNames && h.inspectorNames.length" class="rt-h-sublbl">
                {{ $t("InspectorsRated") || "Baholangan tekshiruvchilar" }}
              </div>
              <div
                v-for="(nm, idx) in h.inspectorNames || []"
                :key="'h' + h.id + 'nm' + idx"
                class="rt-h-iname"
              >
                <i class="far fa-user"></i> {{ nm }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- TAB 3: SEARCH -->
      <div v-else-if="tab === 'search'">
        <section class="rt-card">
          <div class="rt-card-head">
            <h2>
              <span class="rt-step"><i class="fas fa-search"></i></span>
              {{ $t("RatingTabSearch") || "Muloqotni qidirish" }}
            </h2>
            <span class="rt-hint">
              {{ $t("RatingByNumberOnly") || "Faqat tekshiruv raqami bo'yicha" }}
            </span>
          </div>
          <div class="rt-card-body">
            <div class="rt-field">
              <label>{{ $t("InspectionNumber") || "Tekshiruv raqami" }}</label>
              <div class="rt-input-wrap">
                <i class="fas fa-hashtag lead"></i>
                <input
                  v-model="lookupQuery"
                  type="text"
                  placeholder="INS-00000"
                  :disabled="lookupLoading"
                  @keyup.enter="searchInspections"
                />
                <button
                  class="rt-go"
                  :disabled="lookupLoading"
                  @click="searchInspections"
                >
                  <i v-if="lookupLoading" class="fas fa-spinner fa-spin"></i>
                  <span v-else>{{ $t("Search") || "Qidirish" }}</span>
                </button>
              </div>
            </div>

            <div v-if="lookupError" class="rt-error">
              <i class="fas fa-exclamation-circle"></i> {{ lookupError }}
            </div>
          </div>
        </section>

        <div v-if="lookupResults.length" class="rt-history" style="margin-top: 18px">
          <div v-for="item in lookupResults" :key="item.id" class="rt-h-card">
            <div class="rt-h-top">
              <div>
                <div class="rt-h-code">{{ item.docNumber }}</div>
                <div class="rt-h-desc">
                  {{ item.applicationTypeName || item.applicationType }}
                </div>
              </div>
              <div class="rt-h-date">
                <i class="far fa-calendar-alt"></i> {{ formatDate(item.docDate) }}
              </div>
            </div>
            <div class="rt-h-row">
              <span class="rt-h-lbl">{{ $t("contractor") || "Tashkilot nomi" }}</span>
              <span>{{ item.contractor }}</span>
            </div>
            <div class="rt-h-row">
              <span class="rt-h-lbl">{{ $t("organization") || "Tashkilotlar" }}</span>
              <span>{{ item.organization }}</span>
            </div>
            <div class="rt-h-row">
              <span class="rt-h-lbl">{{ $t("employee") || "Xodim" }}</span>
              <span>{{ item.employee }}</span>
            </div>
            <div class="rt-h-row">
              <span class="rt-h-lbl">{{ $t("status") || "Holati" }}</span>
              <span>{{ item.statusName || item.status }}</span>
            </div>
          </div>
        </div>
        <div
          v-else-if="lookupSearched && !lookupLoading && !lookupError"
          class="rt-empty"
        >
          <i class="far fa-clipboard"></i>
          <p>{{ $t("InspectionNotFound") || "Tekshiruv topilmadi" }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RequestRatingService from "@/services/requestrating.service";
import InspectionService from "@/services/inspection.service";

const STORAGE_KEY = "rating_history";

export default {
  name: "RatingPage",
  data() {
    return {
      tab: "submit",
      numberQuery: "",
      searchError: "",
      searching: false,
      found: null,
      inspectionRating: 0,
      inspectionComment: "",
      inspectors: [],
      inspectorsRating: 0,
      inspectorsComment: "",
      submitting: false,
      submitError: "",
      history: [],
      loadingHistory: false,
      lookupQuery: "",
      lookupLoading: false,
      lookupError: "",
      lookupSearched: false,
      lookupResults: [],
      historyFilter: {
        search: "",
        sortBy: "",
        orderType: "",
        page: 1,
        pageSize: 50,
      },
    };
  },
  computed: {
    canSubmit() {
      if (!this.found) return false;
      if (this.inspectionRating < 1) return false;
      if (this.inspectors.length && this.inspectorsRating < 1) return false;
      return true;
    },
  },
  created() {
    this.loadHistory();
    if (this.$route.query.tab === "history") this.tab = "history";
  },
  watch: {
    tab(val) {
      if (val === "history") this.loadHistory();
    },
  },
  methods: {
    loadHistory() {
      this.loadingHistory = true;
      RequestRatingService.GetList(this.historyFilter)
        .then((res) => {
          const rows = (res.data && (res.data.rows || res.data.items)) || [];
          this.history = this.groupHistoryRows(rows);
        })
        .catch(() => {
          try {
            const raw = localStorage.getItem(STORAGE_KEY);
            this.history = raw ? JSON.parse(raw) : [];
          } catch (e) {
            this.history = [];
          }
        })
        .finally(() => {
          this.loadingHistory = false;
        });
    },
    groupHistoryRows(rows) {
      let inspectorCache = {};
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        if (parsed && !Array.isArray(parsed)) inspectorCache = parsed;
      } catch (e) { /* ignore */ }
      const groups = new Map();
      rows.forEach((r) => {
        const key = r.docNumber || r.requestId || "row-" + r.id;
        let g = groups.get(key);
        if (!g) {
          g = {
            id: key,
            code: r.docNumber || "",
            desc: r.contractorName || "",
            contractor: r.contractorName || "",
            submittedAt: "",
            inspectionRating: 0,
            inspectionComment: "",
            ratingTypeName: "",
            inspectorsRating: 0,
            inspectorsComment: "",
            inspectorsTypeName: "",
            inspectorNames: [],
            _latestDate: null,
          };
          groups.set(key, g);
        }
        if (r.isRequest) {
          g.inspectionRating = r.rating || 0;
          g.inspectionComment = r.text || "";
          g.ratingTypeName = r.ratingTypeName || "";
        } else {
          g.inspectorsRating = r.rating || 0;
          g.inspectorsComment = r.text || "";
          g.inspectorsTypeName = r.ratingTypeName || "";
        }
        const rawDate = r.dateOfCreated || r.dateNPCreated || r.createDate || "";
        const parsed = this.parseTicketDate(rawDate);
        if (parsed.date && (!g._latestDate || parsed.date > g._latestDate)) {
          g._latestDate = parsed.date;
          g.submittedAt = parsed.display;
        } else if (!g.submittedAt && parsed.display) {
          g.submittedAt = parsed.display;
        }
      });
      const list = Array.from(groups.values());
      list.forEach((g) => {
        if (g.code && Array.isArray(inspectorCache[g.code])) {
          g.inspectorNames = inspectorCache[g.code];
        }
      });
      list.sort((a, b) => {
        const ta = a._latestDate ? a._latestDate.getTime() : 0;
        const tb = b._latestDate ? b._latestDate.getTime() : 0;
        return tb - ta;
      });
      return list;
    },
    formatDateTime(d) {
      const pad = (n) => n.toString().padStart(2, "0");
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(
        d.getHours()
      )}:${pad(d.getMinutes())}`;
    },
    parseTicketDate(raw) {
      if (!raw) return { date: null, display: "" };
      if (typeof raw === "string") {
        const m = raw.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})(?:[ T](\d{1,2}):(\d{1,2}))?/);
        if (m) {
          const day = parseInt(m[1], 10);
          const mon = parseInt(m[2], 10) - 1;
          const year = parseInt(m[3], 10);
          const hh = m[4] ? parseInt(m[4], 10) : 0;
          const mm = m[5] ? parseInt(m[5], 10) : 0;
          const d = new Date(year, mon, day, hh, mm);
          if (!isNaN(d.getTime())) return { date: d, display: raw };
        }
      }
      const d = new Date(raw);
      if (!isNaN(d.getTime())) return { date: d, display: this.formatDateTime(d) };
      return { date: null, display: String(raw) };
    },
    persistHistory() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.history));
      } catch (e) { /* ignore */ }
    },
    formatDate(d) {
      if (!d) return "";
      const date = new Date(d);
      if (isNaN(date.getTime())) return d;
      const pad = (n) => n.toString().padStart(2, "0");
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
    },
    apiErrorMessage(err, fallbackKey, fallbackText) {
      const data = err && err.response && err.response.data;
      if (data) {
        if (typeof data === "string" && data.trim()) return data;
        if (data.message) return data.message;
        if (data.error) return data.error;
      }
      return this.$t(fallbackKey) || fallbackText;
    },
    searchByNumber() {
      this.searchError = "";
      this.submitError = "";
      const q = (this.numberQuery || "").trim();
      if (!q) {
        this.searchError =
          this.$t("EnterInspectionNumber") || "Tekshiruv raqamini kiriting";
        return;
      }
      this.searching = true;
      this.found = null;
      this.inspectors = [];
      RequestRatingService.GetByDocNumber(q)
        .then((res) => {
          const d = res.data || {};
          const periodStart = this.formatDate(d.checkStartDate);
          const periodEnd = this.formatDate(d.checkEndDate);
          let period = "";
          if (periodStart && periodEnd) period = `${periodStart} — ${periodEnd}`;
          else period = periodStart || periodEnd || "";

          this.found = {
            id: d.id,
            code: d.docNumber || q,
            desc: d.checkType || "",
            contractor: d.contractorName || "",
            inspectionOrganization: d.inspectionOrganization || "",
            date: period,
          };
          const names = Array.isArray(d.inspectorNames) ? d.inspectorNames : [];
          this.inspectors = names.map((name, idx) => ({
            id: idx + 1,
            name: name,
            org: "",
          }));
          this.inspectionRating = 0;
          this.inspectionComment = "";
          this.inspectorsRating = 0;
          this.inspectorsComment = "";
        })
        .catch((err) => {
          this.found = null;
          this.inspectors = [];
          const status = err && err.response && err.response.status;
          if (status === 404) {
            this.searchError =
              this.$t("InspectionNotFound") || "Tekshiruv topilmadi";
          } else {
            this.searchError = this.apiErrorMessage(
              err,
              "InspectionNotFound",
              "Tekshiruv topilmadi"
            );
          }
        })
        .finally(() => {
          this.searching = false;
        });
    },
    searchInspections() {
      this.lookupError = "";
      const q = (this.lookupQuery || "").trim();
      if (!q) {
        this.lookupError =
          this.$t("EnterInspectionNumber") || "Tekshiruv raqamini kiriting";
        return;
      }
      this.lookupLoading = true;
      this.lookupSearched = true;
      this.lookupResults = [];
      InspectionService.SearchByDocNumber(q)
        .then((res) => {
          const d = res.data;
          this.lookupResults = Array.isArray(d) ? d : d ? [d] : [];
        })
        .catch((err) => {
          this.lookupResults = [];
          const status = err && err.response && err.response.status;
          if (status === 404) {
            this.lookupResults = [];
          } else {
            this.lookupError = this.apiErrorMessage(
              err,
              "InspectionNotFound",
              "Tekshiruv topilmadi"
            );
          }
        })
        .finally(() => {
          this.lookupLoading = false;
        });
    },
    resetForm() {
      this.numberQuery = "";
      this.searchError = "";
      this.submitError = "";
      this.found = null;
      this.inspectors = [];
      this.inspectionRating = 0;
      this.inspectionComment = "";
      this.inspectorsRating = 0;
      this.inspectorsComment = "";
    },
    buildPayloads() {
      const docNumber = this.found.code;
      const payloads = [
        {
          docNumber: docNumber,
          rating: this.inspectionRating,
          isRequest: true,
          text: this.inspectionComment || "",
        },
      ];
      if (this.inspectors.length && this.inspectorsRating > 0) {
        payloads.push({
          docNumber: docNumber,
          rating: this.inspectorsRating,
          isRequest: false,
          text: this.inspectorsComment || "",
        });
      }
      return payloads;
    },
    submit() {
      if (!this.canSubmit || this.submitting) return;
      this.submitError = "";
      this.submitting = true;

      const payloads = this.buildPayloads();
      const inspectorNames = this.inspectors.map((i) => i.name);
      Promise.all(payloads.map((p) => RequestRatingService.Create(p)))
        .then(() => {
          if (inspectorNames.length) {
            try {
              const cache = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
              const key = this.found.code;
              cache[key] = inspectorNames;
              localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
            } catch (e) { /* ignore */ }
          }
          this.resetForm();
          this.tab = "history";
          this.loadHistory();
        })
        .catch((err) => {
          this.submitError = this.apiErrorMessage(
            err,
            "RatingSubmitError",
            "Baholashni yuborishda xatolik yuz berdi"
          );
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    formatNow() {
      const d = new Date();
      const pad = (n) => n.toString().padStart(2, "0");
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(
        d.getHours()
      )}:${pad(d.getMinutes())}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.rt-root {
  --rt-bg: #f5f3ee;
  --rt-card: #ffffff;
  --rt-ink: #0e1726;
  --rt-ink-soft: #475569;
  --rt-ink-muted: #94a3b8;
  --rt-line: #e2e5ea;
  --rt-primary: #1e4a8a;
  --rt-primary-dark: #163866;
  --rt-accent: #c9a14a;
  --rt-accent-soft: #f4e9cc;
  --rt-success: #1f7a4d;
  --rt-danger: #b43a3a;

  background: var(--rt-bg);
  color: var(--rt-ink);
  min-height: calc(100vh - 80px);
  font-size: 15px;
  background-image: radial-gradient(
    circle at 1px 1px,
    rgba(14, 23, 38, 0.05) 1px,
    transparent 0
  );
  background-size: 24px 24px;
}
.rt-page {
  max-width: 960px;
  margin: 0 auto;
  padding: 28px 24px 80px;
}
.rt-head {
  margin-bottom: 18px;
}
.rt-title {
  font-size: 28px;
  font-weight: 700;
}
.rt-sub {
  margin-top: 6px;
  color: var(--rt-ink-soft);
  font-size: 14px;
  max-width: 760px;
}

/* tabs */
.rt-tabs {
  display: flex;
  gap: 6px;
  margin-bottom: 16px;
  background: #fff;
  border: 1px solid var(--rt-line);
  border-radius: 12px;
  padding: 6px;
  width: fit-content;
}
.rt-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 18px;
  border-radius: 8px;
  border: 0;
  background: transparent;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  color: var(--rt-ink-soft);
  transition: 0.15s;
  &.active {
    background: var(--rt-ink);
    color: #fff;
  }
  &:hover:not(.active) {
    background: #f0eee7;
    color: var(--rt-ink);
  }
}
.rt-badge-cnt {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  padding: 0 7px;
  background: var(--rt-accent);
  color: #fff;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 800;
}
.rt-tab.active .rt-badge-cnt {
  background: #fff;
  color: var(--rt-ink);
}

/* card */
.rt-card {
  background: var(--rt-card);
  border: 1px solid var(--rt-line);
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(14, 23, 38, 0.04);
  overflow: hidden;
  & + .rt-card { margin-top: 18px; }
}
.rt-card-head {
  padding: 16px 22px;
  border-bottom: 1px solid var(--rt-line);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  h2 {
    font-size: 16px;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0;
  }
}
.rt-step {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--rt-ink);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
}
.rt-hint { font-size: 12px; color: var(--rt-ink-muted); }
.rt-card-body { padding: 20px 22px; }

/* field */
.rt-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--rt-ink-soft);
  }
}
.rt-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  background: #fff;
  border: 1.5px solid var(--rt-line);
  border-radius: 10px;
  transition: 0.2s;
  &:focus-within {
    border-color: var(--rt-primary);
    box-shadow: 0 0 0 4px rgba(30, 74, 138, 0.08);
  }
  .lead {
    position: absolute;
    left: 14px;
    color: var(--rt-ink-muted);
    font-size: 14px;
  }
  input {
    flex: 1;
    border: 0;
    outline: none;
    padding: 12px 12px 12px 38px;
    background: transparent;
    font-size: 14px;
    font-weight: 500;
    color: var(--rt-ink);
  }
}
.rt-go {
  margin-right: 4px;
  background: var(--rt-primary);
  color: #fff;
  border: 0;
  padding: 8px 16px;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: var(--rt-primary-dark); }
}

.rt-error {
  margin-top: 14px;
  padding: 10px 14px;
  background: #fdecec;
  border: 1px solid #f6c0c0;
  color: var(--rt-danger);
  border-radius: 8px;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.rt-found {
  margin-top: 18px;
  padding: 16px;
  background: #f8f6f0;
  border: 1px solid var(--rt-line);
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 14px;
}
.rt-found-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: var(--rt-ink);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}
.rt-found-info { flex: 1; min-width: 0; }
.rt-found-code { font-weight: 800; font-size: 15px; color: var(--rt-primary); }
.rt-found-desc { font-size: 13px; color: var(--rt-ink); margin-top: 2px; }
.rt-found-sub {
  font-size: 12px;
  color: var(--rt-ink-soft);
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 5px;
}
.rt-found-sub-lbl {
  font-weight: 700;
  color: var(--rt-ink);
}
.rt-found-meta {
  font-size: 12px;
  color: var(--rt-ink-muted);
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 5px;
}
.rt-insp-empty {
  border: 1px dashed var(--rt-line);
  border-radius: 10px;
  padding: 18px;
  text-align: center;
  color: var(--rt-ink-muted);
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.rt-go {
  &:disabled {
    background: #b9bcc4;
    cursor: not-allowed;
  }
}
.rt-found-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 20px;
  background: #e8f1ea;
  color: var(--rt-success);
}

/* rate block */
.rt-rate-block {
  border: 1.5px solid var(--rt-line);
  border-radius: 12px;
  padding: 18px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: relative;
  overflow: hidden;
  margin-bottom: 18px;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--rt-accent);
  }
}
.rt-who {
  display: flex;
  align-items: center;
  gap: 10px;
}
.rt-who-icon {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  background: var(--rt-accent-soft);
  color: var(--rt-ink);
  display: flex;
  align-items: center;
  justify-content: center;
}
.rt-who-lbl {
  font-size: 10px;
  color: var(--rt-ink-muted);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 700;
}
.rt-who-nm { font-weight: 700; font-size: 14px; }

.rt-stars {
  display: flex;
  gap: 6px;
  align-items: center;
}
.rt-star {
  width: 30px;
  height: 30px;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
  color: #d8d6ce;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { transform: scale(1.15); }
  &.filled { color: var(--rt-accent); }
}
.rt-stars-val {
  margin-left: 10px;
  font-size: 13px;
  color: var(--rt-ink);
}
.rt-comment {
  width: 100%;
  min-height: 80px;
  border: 1.5px solid var(--rt-line);
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  color: var(--rt-ink);
  resize: vertical;
  background: #fbfaf6;
  font-family: inherit;
  &:focus {
    outline: none;
    border-color: var(--rt-primary);
    background: #fff;
  }
}

/* inspectors collective list */
.rt-insp-names {
  list-style: none;
  margin: 0;
  padding: 10px 12px;
  background: #fafaf6;
  border: 1px solid var(--rt-line);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  li {
    font-size: 13px;
    color: var(--rt-ink);
    display: flex;
    align-items: center;
    gap: 8px;
    i {
      color: var(--rt-ink-muted);
      font-size: 12px;
    }
  }
}

/* inspectors list */
.rt-inspectors-wrap {
  border-top: 1px dashed var(--rt-line);
  padding-top: 18px;
  margin-top: 6px;
}
.rt-inspectors-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
  flex-wrap: wrap;
  label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--rt-ink-soft);
  }
}
.rt-insp-card {
  border: 1.5px solid var(--rt-line);
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 10px;
  background: #fff;
  transition: 0.15s;
  &.selected {
    border-color: var(--rt-primary);
    background: #fafbfd;
  }
}
.rt-insp-row {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
}
.rt-chk {
  width: 18px;
  height: 18px;
  accent-color: var(--rt-primary);
  cursor: pointer;
  flex-shrink: 0;
}
.rt-insp-info { flex: 1; min-width: 0; }
.rt-insp-name {
  font-weight: 700;
  font-size: 14px;
  color: var(--rt-ink);
}
.rt-insp-org {
  font-size: 12px;
  color: var(--rt-ink-soft);
  margin-top: 2px;
}
.rt-insp-rate {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed var(--rt-line);
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* actions */
.rt-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  padding: 16px 22px;
  background: #fafaf6;
  border-top: 1px solid var(--rt-line);
  flex-wrap: wrap;
}
.rt-note {
  font-size: 12px;
  color: var(--rt-ink-soft);
  display: flex;
  align-items: center;
  gap: 8px;
  i { color: var(--rt-ink-muted); }
}
.rt-btns { display: flex; gap: 10px; }
.rt-btn {
  border: 0;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  padding: 10px 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: 0.2s;
  &.ghost {
    background: transparent;
    color: var(--rt-ink-soft);
    border: 1.5px solid var(--rt-line);
    &:hover {
      border-color: var(--rt-ink);
      color: var(--rt-ink);
    }
  }
  &.primary {
    background: var(--rt-ink);
    color: #fff;
    &:hover:not(:disabled) { background: #000; }
    &:disabled {
      background: #b9bcc4;
      cursor: not-allowed;
    }
  }
}

/* empty */
.rt-empty {
  background: #fff;
  border: 1px dashed var(--rt-line);
  border-radius: 14px;
  padding: 50px 22px;
  text-align: center;
  color: var(--rt-ink-muted);
  i { font-size: 40px; margin-bottom: 12px; display: block; }
  p { font-size: 14px; margin: 0; }
}

/* history */
.rt-history { display: flex; flex-direction: column; gap: 12px; }
.rt-h-card {
  background: #fff;
  border: 1px solid var(--rt-line);
  border-radius: 14px;
  padding: 16px 18px;
}
.rt-h-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 10px;
}
.rt-h-code {
  font-weight: 800;
  font-size: 15px;
  color: var(--rt-primary);
}
.rt-h-desc {
  font-size: 13px;
  color: var(--rt-ink-soft);
  margin-top: 2px;
}
.rt-h-date {
  font-size: 12px;
  color: var(--rt-ink-muted);
  white-space: nowrap;
}
.rt-h-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  gap: 12px;
}
.rt-h-lbl {
  font-size: 13px;
  color: var(--rt-ink-soft);
}
.rt-h-stars {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 13px;
  color: #d8d6ce;
  i.on { color: var(--rt-accent); }
  strong { margin-left: 8px; color: var(--rt-ink); }
}
.rt-h-cm {
  font-style: italic;
  font-size: 13px;
  color: var(--rt-ink-soft);
  background: #fafaf6;
  border-left: 3px solid var(--rt-accent);
  padding: 8px 12px;
  margin: 4px 0 6px;
  border-radius: 0 6px 6px 0;
}
.rt-h-inspectors {
  margin-top: 10px;
  border-top: 1px dashed var(--rt-line);
  padding-top: 10px;
}
.rt-h-sublbl {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--rt-ink-muted);
  margin-bottom: 6px;
}
.rt-h-irow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 0;
  gap: 12px;
}
.rt-h-iname {
  font-size: 13px;
  font-weight: 600;
  color: var(--rt-ink);
}

@media (max-width: 720px) {
  .rt-page { padding: 16px 12px 60px; }
  .rt-title { font-size: 22px; }
  .rt-tabs { width: 100%; }
  .rt-tab { flex: 1; justify-content: center; padding: 9px 10px; }
  .rt-actions { flex-direction: column; align-items: stretch; }
  .rt-btns { justify-content: flex-end; }
}
</style>
